"use client";

import cnBind from "classnames/bind";
import { useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent } from "react";
import { useTranslation } from "react-i18next";

import { PARTNER_LOGOS } from "./partnersLogos";

import styles from "./PartnersMarquee.module.scss";

const cx = cnBind.bind(styles);

const AUTO_PX_PER_SEC = 32;
const RESUME_DELAY_MS = 1400;

export const PartnersMarquee = () => {
    const { t } = useTranslation("common");
    const marqueeRef = useRef<HTMLDivElement>(null);
    const offsetRef = useRef(0);
    const halfWidthRef = useRef(0);
    const draggingRef = useRef(false);
    const pausedRef = useRef(false);
    const lastXRef = useRef(0);
    const lastTsRef = useRef(0);
    const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const rafRef = useRef(0);
    const reduceMotionRef = useRef(false);

    const normalizeOffset = useCallback(() => {
        const half = halfWidthRef.current;
        if (half <= 0) return;
        let x = offsetRef.current;
        while (x <= -half) x += half;
        while (x > 0) x -= half;
        offsetRef.current = x;
    }, []);

    const applyTransform = useCallback(() => {
        const el = marqueeRef.current;
        if (!el) return;
        el.style.transform = `translate3d(${offsetRef.current}px,0,0)`;
    }, []);

    const measure = useCallback(() => {
        const el = marqueeRef.current;
        if (!el) return;
        halfWidthRef.current = el.scrollWidth / 2;
        normalizeOffset();
        applyTransform();
    }, [applyTransform, normalizeOffset]);

    const clearResumeTimer = () => {
        if (resumeTimerRef.current) {
            clearTimeout(resumeTimerRef.current);
            resumeTimerRef.current = null;
        }
    };

    const scheduleResume = () => {
        clearResumeTimer();
        resumeTimerRef.current = setTimeout(() => {
            pausedRef.current = false;
            lastTsRef.current = 0;
        }, RESUME_DELAY_MS);
    };

    useEffect(() => {
        reduceMotionRef.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        measure();

        const ro = new ResizeObserver(() => measure());
        if (marqueeRef.current) ro.observe(marqueeRef.current);

        const tick = (ts: number) => {
            if (!lastTsRef.current) lastTsRef.current = ts;
            const dt = Math.min(64, ts - lastTsRef.current) / 1000;
            lastTsRef.current = ts;

            if (!pausedRef.current && !draggingRef.current && !reduceMotionRef.current) {
                offsetRef.current -= AUTO_PX_PER_SEC * dt;
                normalizeOffset();
                applyTransform();
            }

            rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);

        return () => {
            cancelAnimationFrame(rafRef.current);
            ro.disconnect();
            clearResumeTimer();
        };
    }, [applyTransform, measure, normalizeOffset]);

    const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
        if (reduceMotionRef.current) return;
        draggingRef.current = true;
        pausedRef.current = true;
        clearResumeTimer();
        lastXRef.current = e.clientX;
        e.currentTarget.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
        if (!draggingRef.current) return;
        const dx = e.clientX - lastXRef.current;
        lastXRef.current = e.clientX;
        offsetRef.current += dx;
        normalizeOffset();
        applyTransform();
    };

    const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
        if (!draggingRef.current) return;
        draggingRef.current = false;
        try {
            e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
            /* already released */
        }
        scheduleResume();
    };

    const track = [...PARTNER_LOGOS, ...PARTNER_LOGOS];

    return (
        <section className={cx("partners")} aria-label={t("home.partners.ariaLabel")}>
            <div className={cx("head")}>
                <p className={cx("label")}>{t("home.partners.label")}</p>
                <p className={cx("sublabel", "seoOnly")}>{t("home.partners.sublabel")}</p>
            </div>

            <div className={cx("trackShell")}>
                <div className={cx("fade", "fadeLeft")} aria-hidden="true" />
                <div className={cx("fade", "fadeRight")} aria-hidden="true" />

                <div
                    className={cx("track")}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                    onPointerLeave={(e) => {
                        if (draggingRef.current) onPointerUp(e);
                    }}
                >
                    <div ref={marqueeRef} className={cx("marquee")}>
                        {track.map((logo, index) => {
                            const scale = logo.scale ?? 0.86;
                            const card = logo.card ?? "light";
                            const cardClass =
                                card === "red" ? "cardRed" : card === "dark" ? "cardDark" : "cardLight";

                            return (
                                <div
                                    className={cx("logoCard", logo.featured && "logoCardFeatured")}
                                    key={`${logo.src}-${index}`}
                                >
                                    <div
                                        className={cx("logoCardInner", cardClass)}
                                        style={{ ["--logo-scale" as string]: String(scale) }}
                                    >
                                        {/* eslint-disable-next-line @next/next/no-img-element */}
                                        <img
                                            src={logo.src}
                                            alt={logo.alt}
                                            className={cx("logo")}
                                            draggable={false}
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};
